import React, { useState } from 'react';
import type { TextFieldProps } from '@mui/material/TextField';

import IconButton from '@src/shared/ui/Button/IconButton';
import { EyeIcon, EyeOffIcon } from '@src/shared/ui/icons';
import InputAdornment from '@src/shared/ui/inputs/InputAdornment';
import TextInput from './TextInput';

type PasswordFieldProps = TextFieldProps & {
  disablePasswordManager?: boolean;
};

// `-webkit-text-security` is a WebKit/Blink-only property. Firefox has no
// equivalent, so on unsupported browsers we must fall back to a native
// type="password" input instead of silently rendering the password as
// plain text.
function detectWebkitTextSecuritySupport() {
  if (typeof window === 'undefined' || typeof CSS === 'undefined' || typeof CSS.supports !== 'function') {
    return false;
  }
  return CSS.supports('-webkit-text-security', 'disc');
}

const supportsWebkitTextSecurity = detectWebkitTextSecuritySupport();

const PasswordField = React.forwardRef<HTMLDivElement, PasswordFieldProps>(
  (props, ref) => {
    const { disablePasswordManager = false, slotProps, ...restProps } = props;

    const [visible, setVisible] = useState(false);
    const htmlInputSlotProps = (slotProps?.htmlInput ?? {}) as Record<
      string,
      unknown
    >;
    const htmlInputStyle = (htmlInputSlotProps.style ??
      {}) as React.CSSProperties;

    // Only skip the native type="password" masking when we can replace it
    // with the CSS trick; otherwise a browser without `-webkit-text-security`
    // support (e.g. Firefox) would show the password in plain text.
    const canMaskWithoutPasswordType =
      disablePasswordManager && supportsWebkitTextSecurity;

    const handleTogglePasswordVisibility = () => {
      setVisible((current) => !current);
    };

    const handleMouseDownPassword = (event: React.MouseEvent) => {
      event.preventDefault();
    };

    return (
      <TextInput
        autoComplete="new-password"
        {...restProps}
        ref={ref}
        type={canMaskWithoutPasswordType || visible ? 'text' : 'password'}
        slotProps={{
          ...slotProps,
          htmlInput: {
            ...htmlInputSlotProps,
            style: {
              ...htmlInputStyle,
              WebkitTextSecurity:
                canMaskWithoutPasswordType && !visible ? 'disc' : undefined,
            },
          },
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  size="small"
                  aria-label="toggle password visibility"
                  onMouseDown={handleMouseDownPassword}
                  onClick={handleTogglePasswordVisibility}
                >
                  {visible ? (
                    <EyeIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <EyeOffIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </InputAdornment>
            ),
            ...(slotProps?.input as Record<string, unknown>),
          },
        }}
      />
    );
  },
);

PasswordField.displayName = 'PasswordField';
export default PasswordField;
